<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { ChallengeConfiguration } from '@/types/v2/task/challenge/configuration.types';
import { useChallenge } from '@/composables/tasks/challenge/useChallenge';
import { useChallengeConfigurations } from '@/composables/tasks/challenge/useChallengeConfigurations';
import { useChallengeTaskOperations } from '@/composables/tasks/challenge/useChallengeOperations';
import { useChallengeProgress } from '@/composables/tasks/challenge/useChallengeProgress';
import { useRouter } from 'vue-router';
import FooterActions from '@/components/v2/Tasks/Challenge/Buttons/ActionButtons/FooterActions.vue';
import ChallengeModal from '@/components/v2/Tasks/Challenge/Modals/ChallengeModal.vue';
import WeightModal from "@/components/v2/Tasks/Challenge/Modals/WeightModal.vue";
import { useWeight } from "@/composables/tasks/challenge/useChallengeWeight";
import WeightIcon from "@/components/v2/UI/Icons/WeightIcon.vue";
import DocumentIcon from "@/components/v2/UI/Icons/DocumentIcon.vue";
import InfoIcon from "@/components/v2/UI/Icons/InfoIcon.vue";
import { getChallengeMaterialDisplayItems } from "@/utils/v2/tasks/display";
import RandomWeightProgressCard from "@/components/v2/Tasks/Shared/RandomWeightProgressCard.vue";

const router = useRouter();
const selectedConfiguration = ref<ChallengeConfiguration | null>(null);

const {
    isIndividualWeightType,
    isNoWeightType,
    isRandomWeightType,
    getWeighedFish,
    getTargetWeightAmount,
    getWeighedFishInConfiguration
} = useWeight();

// Modals
const challengeModal = ref<boolean>(false);
const weightModal = ref<boolean>(false);
const instructionsModal = ref<boolean>(false);


const toggleInstructionsModal = () => {
    instructionsModal.value = !instructionsModal.value;
};

const openWeightModal = (config?: ChallengeConfiguration | null) => {
    weightModal.value = true;
    selectedConfiguration.value = config || null;
}

const openChallengeModal = (config: ChallengeConfiguration) => {
    challengeModal.value = true;
    selectedConfiguration.value = config;
}

const goToEquipmentManagement = () => {
    router.push({ name: "ChallengeEquipmentView" });
}

const { configurations } = useChallenge();
const { getConfigurationTags } = useChallengeConfigurations();
const { getTagStyles } = useChallengeTaskOperations();
const { getConfigurationChallengeProgress, getConfigurationWeightProgress } = useChallengeProgress();

const getProgress = (configuration: ChallengeConfiguration): number => {
    return getConfigurationChallengeProgress(configuration.key);
};

const getWeightProgress = (configuration: ChallengeConfiguration): number => {
    return getConfigurationWeightProgress(configuration.key);
};

const getProgressPercentage = (configuration: ChallengeConfiguration): number => {
    const progress = getProgress(configuration);
    const target = configuration.targetAmount;
    return Math.min(Math.round((progress / target) * 100), 100);
};

const getWeightProgressPercentage = (configuration: ChallengeConfiguration): number => {
    const progress = getWeightProgress(configuration);
    const target = configuration.targetWeightAmount ?? 0;
    return Math.min(Math.round((progress / target) * 100), 100);
};

const isCompleted = (configuration: ChallengeConfiguration): boolean => {
    return getProgress(configuration) >= configuration.targetAmount;
};

// Special instructions state
const showInstructions = ref<Record<string, boolean>>({});

// Initialize instructions as expanded by default
watch(() => configurations.value, (configs) => {
    if (configs) {
        configs.forEach((config) => {
            if (config.instructions && showInstructions.value[config.key] === undefined) {
                showInstructions.value[config.key] = true;
            }
        });
    }
}, { immediate: true });

const toggleInstructions = (configKey: string) => {
    showInstructions.value[configKey] = !showInstructions.value[configKey];
};

const getConfigInstructions = (config: ChallengeConfiguration): string | undefined => {
    return config.instructions;
};

const standardConfigurations = computed(() => {
    return configurations.value.filter((config) => !('isSimplifiedBath' in config || 'isStandardBath' in config));
});

const instructionsColspan = computed(() => {
    const base = 10;
    return isIndividualWeightType.value ? base : base - 1;
});

const getMaterialsDisplay = (config: ChallengeConfiguration) => {
    return getChallengeMaterialDisplayItems(config);
};
</script>

<template>
    <div class="space-y-6 relative">
        <!-- Header Actions -->
        <div class="flex justify-between items-center mb-2 w-full">
            <div>
                <h1 class="text-2xl font-bold text-gray-800">Challenge Administration</h1>
            </div>
            <div class="flex justify-end gap-4">
                <button
                    @click="goToEquipmentManagement"
                    class="px-4 py-2 bg-primary text-white flex items-center gap-2 rounded-lg shadow-sm hover:bg-primary/90 transition-colors duration-200 font-medium text-sm">
                    <InfoIcon />
                    <span>Equipment</span>
                </button>

                <button
                    v-if="!isIndividualWeightType && !isNoWeightType"
                    @click="openWeightModal()"
                    class="px-4 py-2 bg-primary text-white flex items-center gap-2 rounded-lg shadow-sm hover:bg-primary/90 transition-colors duration-200 font-medium text-sm">
                    <WeightIcon />
                    Record Weight
                </button>
                <button
                    @click="router.push({name: 'ChallengeWeights'})"
                    class="px-4 py-2 bg-primary text-white flex items-center gap-2 rounded-lg shadow-sm hover:bg-primary/90 transition-colors duration-200 font-medium text-sm">
                    <DocumentIcon />
                    View Weight Records
                </button>
            </div>
        </div>

        <RandomWeightProgressCard
            v-if="isRandomWeightType"
            class="sticky top-2 z-10"
            :current="getWeighedFish"
            :target="getTargetWeightAmount"
        />

        <span class="w-full bg-secondary h-[1px] block"></span>
        <!-- Challenge Table (Injection) -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div class="overflow-x-auto">
                <div v-if="standardConfigurations.length === 0" class="text-center py-8 text-gray-500">
                    No configurations available
                </div>

                <table v-else class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                    <tr>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source Tank</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source Tags</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Destination Tank</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Destination Tags</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Challenge Material</th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dose (mL)</th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                            <th v-if="isIndividualWeightType" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Weight</th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                    <template v-for="config in standardConfigurations" :key="config.key">
                        <tr class="hover:bg-gray-50">
                            <!-- Source Tank -->
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {{ config.sourceTank?.name || config.sourceTank?.label || 'None' }}
                            </td>

                            <!-- Source Tags -->
                            <td class="px-6 py-4 text-sm">
                                <div class="flex flex-wrap gap-1">
                                    <span
                                        v-for="tag in (config.sourceGroup?.tags || []).filter(tag => tag.title !== 'Start Tag')"
                                        :key="tag.key"
                                        class="inline-block px-2 py-0.5 text-xs font-medium rounded-full"
                                        :style="getTagStyles(tag)"
                                    >
                                        {{ tag.title }}
                                    </span>
                                    <span
                                        v-if="!config.sourceGroup?.tags || config.sourceGroup.tags.filter(tag => tag.title !== 'Start Tag').length === 0"
                                        class="inline-block px-2 py-0.5 text-xs font-medium rounded-full opacity-75 bg-gray-400 text-gray-700"
                                    >
                                        Untagged
                                    </span>
                                </div>
                            </td>

                            <!-- Destination Tank -->
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {{ config.destinationTank?.name || config.destinationTank?.label || 'None' }}
                            </td>

                            <!-- Destination Tags -->
                            <td class="px-6 py-4 text-sm">
                                <div class="flex flex-wrap gap-1">
                                    <span
                                        v-for="tag in getConfigurationTags(config)"
                                        :key="tag.key"
                                        class="inline-block px-2 py-0.5 text-xs font-medium rounded-full"
                                        :style="getTagStyles(tag)"
                                    >
                                        {{ tag.title }}
                                    </span>
                                    <span v-if="getConfigurationTags(config).length === 0" class="text-gray-400 text-xs">No tags</span>
                                </div>
                            </td>

                            <!-- Challenge Material -->
                            <td class="px-6 py-4 text-sm text-gray-900">
                                <div v-if="getMaterialsDisplay(config).length > 0">
                                    <div v-for="(material, mIdx) in getMaterialsDisplay(config)" :key="mIdx" class="mb-1 last:mb-0">
                                        {{ material.name }}
                                    </div>
                                </div>
                                <div v-else class="text-gray-500">None</div>
                            </td>

                            <!-- Method -->
                            <td class="px-6 py-4 text-sm text-gray-900">
                                <div v-if="getMaterialsDisplay(config).length > 0">
                                    <div v-for="(material, mIdx) in getMaterialsDisplay(config)" :key="mIdx" class="mb-1 last:mb-0">
                                        {{ material.method }}
                                    </div>
                                </div>
                                <div v-else class="text-gray-500">None</div>
                            </td>

                            <!-- Dose -->
                            <td class="px-6 py-4 text-sm text-gray-900">
                                <div v-if="getMaterialsDisplay(config).length > 0">
                                    <div v-for="(material, mIdx) in getMaterialsDisplay(config)" :key="mIdx" class="mb-1 last:mb-0">
                                        {{ material.dose }}
                                    </div>
                                </div>
                                <div v-else class="text-gray-500">N/A</div>
                            </td>

                            <!-- Progress -->
                            <td class="px-6 py-4 text-sm">
                                <div class="space-y-1">
                                    <div class="flex items-center gap-2">
                                        <div class="w-16 bg-gray-200 rounded-full h-2">
                                            <div
                                                class="h-2 rounded-full transition-all duration-500"
                                                :class="getProgressPercentage(config) === 100 ? 'bg-green-500' : 'bg-primary'"
                                                :style="{ width: `${getProgressPercentage(config)}%` }"
                                            ></div>
                                        </div>
                                        <span class="font-medium" :class="isCompleted(config) ? 'text-green-600' : 'text-gray-900'">
                                            {{ getProgress(config) }}/{{ config.targetAmount }}
                                        </span>
                                    </div>
                                </div>
                            </td>

                            <!-- Weight -->
                            <td v-if="isIndividualWeightType" class="px-6 py-4 text-sm">
                                <div v-if="config.targetWeightAmount" class="space-y-1">
                                    <div class="flex items-center gap-2">
                                        <div class="w-16 bg-gray-200 rounded-full h-2">
                                            <div
                                                class="h-2 rounded-full transition-all duration-500"
                                                :class="getWeightProgressPercentage(config) === 100 ? 'bg-green-500' : 'bg-primary'"
                                                :style="{ width: `${getWeightProgressPercentage(config)}%` }"
                                            ></div>
                                        </div>
                                        <span
                                            class="font-medium"
                                            :class="getWeighedFishInConfiguration(config) >= (config.targetWeightAmount || 0) ? 'text-green-600' : 'text-gray-900'"
                                        >
                                            {{ getWeighedFishInConfiguration(config) }}/{{ config.targetWeightAmount }}
                                        </span>
                                    </div>
                                </div>
                                <span v-else class="text-gray-400">-</span>
                            </td>

                            <!-- Actions -->
                            <td class="px-6 py-4 whitespace-nowrap text-sm">
                                <div class="flex gap-2">
                                    <button
                                        v-if="config.targetWeightAmount"
                                        @click="openWeightModal(config)"
                                        class="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                    >
                                        Record Weight
                                    </button>
                                    <button
                                        @click="openChallengeModal(config)"
                                        class="px-3 py-1.5 text-xs font-medium text-white bg-primary border border-transparent rounded-md hover:bg-primary/90 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                    >
                                        Administer Challenge
                                    </button>
                                </div>
                            </td>
                        </tr>

                        <!-- Special Instructions Row -->
                        <tr v-if="getConfigInstructions(config)" class="bg-gray-50">
                            <td
                                :colspan="instructionsColspan"
                                :class="showInstructions[config.key] ? 'px-6 py-4' : 'px-6 py-1.5'"
                            >
                                <button
                                    @click="toggleInstructions(config.key)"
                                    class="w-full flex items-center justify-between text-left hover:bg-gray-100 rounded transition-colors duration-200"
                                    :class="showInstructions[config.key] ? 'p-3' : 'p-1.5'"
                                >
                                    <div class="flex items-center gap-2">
                                        <svg class="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                                        </svg>
                                        <span :class="showInstructions[config.key] ? 'text-sm font-semibold text-red-600' : 'text-xs font-semibold text-red-600'">
                                            Special Instructions
                                        </span>
                                    </div>
                                    <svg
                                        class="w-4 h-4 text-gray-500 transition-transform duration-200"
                                        :class="{ 'rotate-180': showInstructions[config.key] }"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                <div
                                    v-show="showInstructions[config.key]"
                                    class="mt-2 p-4 bg-red-50 border border-red-200 rounded-lg transition-all duration-300 ease-in-out"
                                >
                                    <p class="text-sm text-red-700 leading-relaxed whitespace-pre-wrap">
                                        {{ getConfigInstructions(config) }}
                                    </p>
                                </div>
                            </td>
                        </tr>
                    </template>
                    </tbody>
                </table>
            </div>
        </div>
    </div>

    <!-- Modals  -->
    <ChallengeModal
        v-if="selectedConfiguration"
        :show="challengeModal"
        :configuration="selectedConfiguration as ChallengeConfiguration"
        v-on:close="() => challengeModal = false"
    />

    <WeightModal
        :show="weightModal"
        :configuration="selectedConfiguration"
        v-on:close="() => weightModal = false"
    />

    <!-- Footer actions -->
    <FooterActions class="border-t border-gray-200">
        <template #start>
            <button
                @click="toggleInstructionsModal"
                class="w-full">
                Instructions
            </button>
        </template>
        <template #middle>
            <button
                @click="router.push({ name: 'ChallengeConfigurations' })"
                class="w-full"
            >
                Back
            </button>
        </template>
        <template #end>
            <button
                @click="router.push({ name: 'ChallengeOverview' })"
                class="w-full"
            >
                Overview
            </button>
        </template>
    </FooterActions>
</template>
